import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Mail, CheckCircle, X, Loader2 } from "lucide-react";
import { API, authFetch } from "@/App";

export function ReferralsDialog({ 
  open, 
  onOpenChange, 
  referrals = [],
  referralStats = { total: 0, pending: 0, approved: 0 },
  onRefresh
}) {
  const [processingReferral, setProcessingReferral] = useState(null);

  const handleUpdateReferralStatus = async (referralId, status) => {
    setProcessingReferral(referralId);
    try {
      const response = await authFetch(`${API}/referrals/${referralId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Referral marked as ${status}`);
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingReferral(null);
    }
  };

  const handleApproveReferral = async (referralId) => {
    setProcessingReferral(referralId);
    try {
      const response = await authFetch(`${API}/referrals/${referralId}/approve`, {
        method: "POST"
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Referral approved! Reward coupon sent.");
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve referral");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingReferral(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Gift className="w-5 h-5 mr-2 text-green-600" />
            Referral Management
          </DialogTitle>
          <DialogDescription>
            Review and manage client referrals. Approve referrals to send reward coupons.
          </DialogDescription>
        </DialogHeader>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 py-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{referralStats.total}</p>
            <p className="text-sm text-gray-600">Total Referrals</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">{referralStats.pending}</p>
            <p className="text-sm text-gray-600">Pending Review</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{referralStats.approved}</p>
            <p className="text-sm text-gray-600">Approved</p>
          </div>
        </div>
        
        {/* Referrals List */}
        <div className="flex-1 overflow-y-auto space-y-3">
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No referrals yet</p>
            </div>
          ) : (
            referrals.map((ref) => (
              <div 
                key={ref.referral_id} 
                className={`p-4 rounded-lg border ${
                  ref.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                  ref.status === 'approved' ? 'bg-green-50 border-green-200' :
                  'bg-gray-50 border-gray-200'
                }`}
                data-testid={`referral-${ref.referral_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{ref.referred_name}</span>
                      <Badge className={`text-xs ${
                        ref.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        ref.status === 'approved' ? 'bg-green-100 text-green-700' :
                        ref.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {ref.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{ref.referred_email}</p>
                    {ref.referred_phone && (
                      <p className="text-sm text-gray-500">{ref.referred_phone}</p>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      <span>Referred by: <strong>{ref.referrer_name}</strong> ({ref.referrer_email})</span>
                      <br />
                      <span>Date: {new Date(ref.created_at).toLocaleDateString()}</span>
                    </div>
                    {ref.message && (
                      <p className="mt-2 text-sm text-gray-600 italic bg-white p-2 rounded">
                        "{ref.message}"
                      </p>
                    )}
                    {ref.referred_coupon && (
                      <p className="mt-2 text-xs">
                        <span className="text-gray-500">Referral Coupon:</span>{' '}
                        <code className="bg-gray-100 px-2 py-1 rounded">{ref.referred_coupon}</code>
                        <span className="text-gray-400"> ($25 off for new client)</span>
                      </p>
                    )}
                    {ref.referrer_reward_coupon && (
                      <p className="text-xs">
                        <span className="text-gray-500">Reward Coupon:</span>{' '}
                        <code className="bg-green-100 px-2 py-1 rounded text-green-700">{ref.referrer_reward_coupon}</code>
                        <span className="text-gray-400"> ($25 off sent to referrer)</span>
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {ref.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleUpdateReferralStatus(ref.referral_id, 'contacted')}
                          disabled={processingReferral === ref.referral_id}
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          Contacted
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApproveReferral(ref.referral_id)}
                          disabled={processingReferral === ref.referral_id}
                        >
                          {processingReferral === ref.referral_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          Approve
                        </Button>
                      </>
                    )}
                    {ref.status === 'contacted' && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproveReferral(ref.referral_id)}
                        disabled={processingReferral === ref.referral_id}
                      >
                        {processingReferral === ref.referral_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        )}
                        Approve
                      </Button>
                    )}
                    {(ref.status === 'pending' || ref.status === 'contacted') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleUpdateReferralStatus(ref.referral_id, 'expired')}
                        disabled={processingReferral === ref.referral_id}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Expire
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
